import { Component, OnInit } from '@angular/core';
import { RecordingsMainService } from '../recordings-main.service';
import { formatDate, formatPercent } from '@angular/common';
import { MonitorData, RecordingFlagDTO } from '../recordings.model';

@Component({
  selector: 'cv-recordings-details',
  templateUrl: './recordings-details.component.html',
  styleUrls: ['./recordings-details.component.scss']
})
export class RecordingsDetailsComponent implements OnInit {
    public cvAngularDateFormat: string = window["CVAngularDateFormat"];
    public cvAngularTimeFormat: string = window["CVAngularTimeFormat"];

    public selectedApplicationId: number = 0;
    public isCalibration: boolean = false;

    public editingOtherId: boolean = false;
    public editedOtherId: string;
    public isSavingOtherId: boolean = false;
    public editingFlags: boolean = false;
    public editedFlagIds: number[] = [];
    public isSavingFlags: boolean = false;

    get MonitoringData(): MonitorData[] {
        return this.recordingsMainService.recordingForDetails?.MonitorData?.filter(o => o.ApplicationDto) ?? [];
    }
    get CalibrationData(): MonitorData[] {
        return this.recordingsMainService.recordingForDetails?.MonitorData?.filter(o => o.MonitoredRecordingDto) ?? [];
    }
    get AllowRemoveMonitoring(): boolean {
        return !this.recordingsMainService.recordingForDetails?.MonitorData?.find(o => o.MonitoredRecordingDto);
    }

    constructor(
        public recordingsMainService: RecordingsMainService,
    ) {
    }

    ngOnInit(): void {
        // Pull panel accessess for monPa, calPa, updPa

        // This timeout prevents an "ExpressionChangedAfterItHasBeenCheckedError"
        setTimeout(() => {
            this.runDataPulls();
        });
    }

    /**
     * Shifts to the previous loaded recording if one exists.
     * */
    public openPreviousRecording(): void {
        if (this.recordingsMainService.IndexForDetails <= 0)
            return;

        this.editingOtherId = false;
        this.editingFlags = false;

        if (this.selectedApplicationId > 0)
            this.handleFormClose(false);

        this.recordingsMainService.recordingForDetails = this.recordingsMainService.recordings[this.recordingsMainService.IndexForDetails - 1];

        // TODO: Add back async? And call service.loadFlags() for new record. Then populate the variable

        this.runDataPulls();
    }

    /**
     * Shifts to the next loaded recording if one exists.
     * */
    public openNextRecording(): void {
        if (this.recordingsMainService.IndexForDetails >= this.recordingsMainService.recordings.length - 1)
            return;

        this.editingOtherId = false;
        this.editingFlags = false;

        if (this.selectedApplicationId > 0)
            this.handleFormClose(false);

        this.recordingsMainService.recordingForDetails = this.recordingsMainService.recordings[this.recordingsMainService.IndexForDetails + 1];

        // TODO: Add back async? And call service.loadFlags() for new record. Then populate the variable

        this.runDataPulls();
    }

    /**
     * Returns a string formatted to the current ClearView Angular date format.
     * @param dateToFormat Date object
     */
    public formatDateField(dateToFormat: Date): string {
        if (!dateToFormat)
            return "";

        return formatDate(dateToFormat, this.cvAngularDateFormat, "en-US")
    }

    /**
     * Returns a string formatted to the current ClearView Angular time format.
     * @param dateToFormat Date object
     */
    public formatTimeField(dateToFormat: Date): string {
        if (!dateToFormat)
            return "";

        return formatDate(dateToFormat, this.cvAngularTimeFormat, "en-US")
    }

    /**
     * Returns a string formatted to standard percent format.
     * @param numberToFormat Number value
     */
    public formatPercentField(numberToFormat: number): string {
        if (!numberToFormat)
            return "0";

        return formatPercent(numberToFormat, "en-US", '0.2-2'); //digitsInfo from Angular docs in format {minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}
    }

    /**
     * Returns a comma-separated list of the names of the specified array of flags 
     * @param flags RecordingFlagDTO array
     */
    public formatFlagNames(flags: RecordingFlagDTO[]): string {
        if (!flags)
            return "";

        return flags.map(o => o.RecordingFlagName).join(', ');
    }

    /**
     * Returns -> separated string of specified group type names.
     * @param emp True to get Emp names, false to get Rec names; defaults to true
     */
    public formatGroupNames(emp: boolean = true): string {
        let groupNames = [];

        for (let i = 5; i > 0; i--) {
            let groupName = this.recordingsMainService.recordingForDetails[emp ? `EmpGroup${i}Name` : `RecGroup${i}Name`];
            if (groupName)
                groupNames.push(groupName);
        }

        let groupNamesString = groupNames.join(" -> ");

        if (groupNamesString == "")
            groupNamesString = this.recordingsMainService.localization.get(emp ? "Unknown_Group_Value" : "Unassigned");

        return groupNamesString;
    }

    /**
     * Opens the current recording's flag assignment field for editing.
     * */
    public openFlagsForEditing(): void {
        this.editedFlagIds = this.recordingsMainService.recordingForDetails.Flags.map(o => o.RecordingFlagID);
        this.editingFlags = true;
    }

    /**
     * Sets the current recording's Other ID to the current value of editedOtherId to the database.
     * */
    public async updateOtherId(): Promise<void> {
        if (this.isSavingOtherId)
            return;

        // No need to save if nothing changed
        if (this.recordingsMainService.recordingForDetails.OtherID == this.editedOtherId) {
            this.editingOtherId = false;
            return;
        }

        try {
            this.isSavingOtherId = true;
            await this.recordingsMainService.editOtherId(this.recordingsMainService.recordingForDetails.RecordingID, this.editedOtherId);

            this.recordingsMainService.recordingForDetails.OtherID = this.editedOtherId;
            this.editingOtherId = false;
        }
        catch (error) {
            console.log(error);
        }
        finally {
            this.isSavingOtherId = false;
        }
    }

    /**
     * Sets the current recording's flag assignments to the ones whose FlagRecordingIds are in the editedFlagId array to the database.
     * */
    public async updateFlags(): Promise<void> {
        if (this.isSavingFlags)
            return;

        // No need to save if nothing changed
        if (JSON.stringify(this.recordingsMainService.recordingForDetails.Flags.map(o => o.RecordingFlagID)) == JSON.stringify(this.editedFlagIds)) {
            this.editingFlags = false;
            return;
        }

        try {
            this.isSavingFlags = true;
            let editedFlags = this.recordingsMainService.flags.filter(o => this.editedFlagIds.includes(o.RecordingFlagID));
            await this.recordingsMainService.editFlags(this.recordingsMainService.recordingForDetails.RecordingID, editedFlags);

            this.recordingsMainService.recordingForDetails.Flags = editedFlags;
            this.editingFlags = false;
        }
        catch (error) {
            console.log(error);
        }
        finally {
            this.isSavingFlags = false;
        }
    }

    /**
     * Runs data pulls necessary for the details view of the current recording. Will skip if it has been done successfully already on this page load.
     * */
    public runDataPulls(): void {
        if (this.recordingsMainService.recordingForDetails.ExtraDataPulled)
            return;

        // Check for audio type and if so then load the url needed and set it
        if (this.recordingsMainService.recordingForDetails.RecordingType == 0) {
            this.recordingsMainService.isUrlLoading = true;
            this.recordingsMainService.loadUrl(this.recordingsMainService.recordingForDetails.RecordingID).then((returnObj) => {
                this.recordingsMainService.recordingForDetails.AudioUrl = returnObj;
                this.recordingsMainService.recordingForDetails.ExtraDataPulled = true;
            }).catch((error) => {
                console.log(error);
            }).finally(() => {
                this.recordingsMainService.isUrlLoading = false;
            });
        }

        if (!this.recordingsMainService.recordingForDetails.Flags) {
            this.recordingsMainService.areFlagsLoading = true;
            this.recordingsMainService.loadFlags(this.recordingsMainService.recordingForDetails.RecordingID).then((returnObj) => {
                this.recordingsMainService.recordingForDetails.Flags = returnObj;
                this.recordingsMainService.recordingForDetails.ExtraDataPulled = true;
            }).catch((error) => {
                console.log(error);
            }).finally(() => {
                this.recordingsMainService.areFlagsLoading = false;
            });
        }

        this.recordingsMainService.isMonitoringDataLoading = true;
        this.loadMonitoringData().then((returnObj) => {
            this.recordingsMainService.recordingForDetails.ExtraDataPulled = true;
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            this.recordingsMainService.isMonitoringDataLoading = false;
        });
    }

    /**
     * Hides monitoring data section and displays specified form.
     * @param appId ID of the form to display
     * @param isCal True if the form is from a calibration, false if otherwise
     */
    public displayFormClick(appId: number, isCal: boolean): void {
        this.selectedApplicationId = appId;
        this.isCalibration = isCal;
    }

    /**
     * Pulls the monitoring data for the currently opened recording.
     * */
    public async loadMonitoringData(): Promise<void> {
        // Check if monitorable and if so load the form and calibration data
        if (this.recordingsMainService.recordingForDetails.Monitorable)
            this.recordingsMainService.recordingForDetails.MonitorData = await this.recordingsMainService.loadMonitoringData(this.recordingsMainService.recordingForDetails.RecordingID);
    }

    /**
     * Toggles the state of the Monitorable flag on the currently opened recording and pushes that change to the database.
     * */
    public toggleMonitoringFlag(): void {
        this.recordingsMainService.recordingForDetails.Monitorable = !this.recordingsMainService.recordingForDetails.Monitorable;

        // Service call with new current value
        this.recordingsMainService.editAllowMonitoring(this.recordingsMainService.recordingForDetails.RecordingID, this.recordingsMainService.recordingForDetails.Monitorable);

        this.loadMonitoringData();
    }

    public handleAudioDownload(): void {
        if (this.recordingsMainService.recordingForDetails?.AudioUrl && this.recordingsMainService.recordingForDetails.AudioUrl != "") {
            let downloadPath = "";
            if (this.recordingsMainService.recordingForDetails.AudioUrl.includes("cvplayrecordingsva1")) // Focus recordings
                downloadPath = this.recordingsMainService.recordingForDetails.AudioUrl.replace("PlayRecording.aspx", "DownloadRecording.aspx");
            else // Non-Focus
                downloadPath = this.recordingsMainService.recordingForDetails.AudioUrl.replace("RecordingPlayer.aspx", "RecordingDownloader.aspx");

            const fileName = this.recordingsMainService.recordingForDetails.ContactID + ".mp3";
            const link = document.createElement('a');
            link.setAttribute('download', fileName);
            link.setAttribute('href', downloadPath);
            link.click();

            URL.revokeObjectURL(link.href);
            link.remove();
        }
    }

    /**
     * Returns a localized string detailing a form's score.
     * @param monitorData MonitorData containing form score submission data
     * @param monitoredDto True to use the monitored recording data in the MonitorData, false to use the calibrated recording data
     */
    public formScoreString(monitorData: MonitorData, monitoredDto: boolean): string {
        let baseString = this.recordingsMainService.localization.get("Form_Score_String");

        let recordingDto = monitoredDto ? monitorData.MonitoredRecordingDto : monitorData.CalibrationRecordingDto;

        baseString = baseString.replace(`{0}`, monitorData.ApplicationDto.ApplicationName);
        baseString = baseString.replace(`{1}`, this.formatPercentField(recordingDto.Score).toString());
        baseString = baseString.replace(`{2}`, this.formatDateField(recordingDto.DateMonitored_UTC));   //chaning DateMonitored to save in MST and UTC will be converted to localtime in the DAL fill
        baseString = baseString.replace(`{3}`, this.formatTimeField(recordingDto.DateMonitored_UTC));
        baseString = baseString.replace(`{4}`, recordingDto.DisplayMonitorName);

        return baseString;
    }

    /**
     * Performs necessary cleanup after a form display has been closed.
     * @param refreshForms If true, current recording's monitoring data will be reloaded.
     */
    public handleFormClose(refreshForms: boolean): void {
        this.selectedApplicationId = 0;
        this.isCalibration = false;

        if (refreshForms)
            this.loadMonitoringData();
    }
}
